// AssetListItemVerticalEditable.tsx (Label-Value-Pair layout with old and editable new value)
import React from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableHead,
  TableBody,
  Paper
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEndorsmentFormData,
  selectPolicyDetails,
  updateEndorsementFormData
} from 'src/features/rfqDataSlice';
import { customComponentMap, customComponentSettings } from './assetListCustomComponents.js';
import FireFloaterEditable from './FireFloaterEditable';
import moment from 'moment';

function AssetListItemVerticalEditable({ sumInsuredData, groupDetails, productId = '', saveRow = () => { } }) {
  const [vehicleModelData, setVehicleModelData] = React.useState([]);
  const [vehicleVariantData, setVehicleVariantData] = React.useState([]);
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);
  const policyDetails = useSelector(selectPolicyDetails);

  const sectionId = sumInsuredData?.id;
  const groupId = groupDetails?.id;

  const selectedHeads = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredSelectedHeads || [];
  const tableListDatas = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredEntry || [];
  const originalList = policyDetails?.policySumInsuredSections.filter(item => item.id == sectionId) || [];

  const handleChange = (attr, index, value) => {
    const actualValue = value?.target?.value ?? value;
    const updatedEntryList = [...tableListDatas];
    updatedEntryList[index] = {
      ...updatedEntryList[index],
      [attr]: actualValue,
      savedStatus: false,
      actionType: 'update'
    };

    const updatedGroup = {
      ...endorsementFormData.sumInsuredSectionData[sectionId].groups[groupId],
      sumInsuredEntry: updatedEntryList
    };

    const updatedSection = {
      ...endorsementFormData.sumInsuredSectionData[sectionId],
      groups: {
        ...endorsementFormData.sumInsuredSectionData[sectionId].groups,
        [groupId]: updatedGroup
      }
    };

    dispatch(
      updateEndorsementFormData({
        sumInsuredSectionData: {
          ...endorsementFormData.sumInsuredSectionData,
          [sectionId]: updatedSection
        }
      })
    );
  };

  const renderEditableCell = (attr, value, index, head) => {
    if (["dateOfManufacture", "purchaseDate", "installationDate", "expectedDateOfCommencementOfErection"].includes(attr)) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={['year', 'month', 'day']}
            inputFormat="dd-MM-yyyy"
            value={value ? moment(value, "DD-MM-YYYY").toDate() : null}
            onChange={(val) => {
              const formattedDate = val ? moment(val).format("DD-MM-YYYY") : null;
              handleChange(attr, index, formattedDate);
            }}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField {...params} size="small" fullWidth />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (["wetRisk", "hillyAreas", "undergroundWork"].includes(attr)) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{head.title || attr}</InputLabel>
          <Select
            label={head.title || attr}
            value={value || ''}
            onChange={(e) => handleChange(attr, index, e)}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    const CustomComponent = customComponentMap[attr];
    const CustomComponentSetting = customComponentSettings[attr];
    if (CustomComponent) {
      const commonProps = {
        tableListDatas,
        index,
        productId,
        saveRow,
        label: head.title || head.sumInsuredHeader.title || attr,
        apiUrl: CustomComponentSetting,
        handleChange
      };
      const extraProps =
        attr === 'vehicleModel' ? { vehicleModelData, setVehicleModelData, setVehicleVariantData } :
          attr === 'vehicleVariant' ? { vehicleModelData, vehicleVariantData, setVehicleVariantData } :
            attr === 'vehicleMake' ? { setVehicleModelData } :
              {};

      return <CustomComponent {...commonProps} {...extraProps} />;
    }

    return (
      <TextField
        size="small"
        fullWidth
        type={typeof value === 'number' || attr.toLowerCase().includes('amount') || attr.toLowerCase().includes('sum') ? 'number' : 'text'}
        value={value || ''}
        onChange={(e) => handleChange(attr, index, e)}
      />
    );
  };

  const renderValue = (attr, value) => {
    if (!value) return 'N/A';
    if (["dateOfManufacture", "purchaseDate", "installationDate"].includes(attr)) {
      return new Date(value).toLocaleDateString('en-GB');
    }
    return Array.isArray(value) ? value.join(', ') : value;
  };

  return (
    <>
      {sumInsuredData?.isFireFloaterEnabled && (
        <FireFloaterEditable
          sectionId={sectionId} />
      )}
      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Field</strong></TableCell>
              <TableCell sx={{ color: 'red' }}><strong>Old Value</strong></TableCell>
              <TableCell sx={{ color: 'green' }}><strong>New Value</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sumInsuredData?.sumInsuredHeaderSorted?.map((head) => {
              const attr = head.sumInsuredHeader.attribute;
              const label = head.title || head.sumInsuredHeader.title;

              const isEditable = selectedHeads.includes(attr);
              if (!isEditable) return null;

              const originalValue = originalList?.[0]?.['assetList']?.[0]?.[attr];
              const newValue = tableListDatas?.[0]?.[attr];

              return (
                <TableRow key={attr}>
                  <TableCell style={{ width: '470px' }}>{label}</TableCell>

                  <TableCell style={{ width: '450px' }} sx={{ color: originalValue !== newValue ? 'red' : 'inherit' }}>{renderValue(attr, originalValue)}</TableCell>
                  <TableCell>
                    {renderEditableCell(attr, newValue, 0, head)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default AssetListItemVerticalEditable;
